/**
 * Pure helpers for the Gang-Sheet Builder page. Kept separate from the component so the
 * status semantics + summary math are unit-tested without a DOM.
 */
import type { PressMethod } from '@fsp/contracts';
import type { components } from '@/lib/api/types.gen';
import { type BlankName, pullBlankLabel, pullBlankVariant, sizeRank } from './inventory-lib';
import { relativeAge } from './orders-queue';

export type BatchSummary = components['schemas']['BatchSummary'];
export type BatchDetail = components['schemas']['BatchDetail'];
/** One garment+location produced by screen print off the sheet (inlined into BatchSummary). */
export type ScreenPrintWorkItem = NonNullable<BatchSummary['screenPrintWork']>[number];
export type BatchItemSummary = components['schemas']['BatchItemSummary'];
export type GangSheetResult = components['schemas']['GangSheetResult'];
export type BatchStatus = BatchSummary['status'];
export type BatchManifest = components['schemas']['BatchManifest'];
// The transfer schema is inlined into BatchManifest (not a top-level named component), so derive it.
export type BatchManifestTransfer = BatchManifest['transfers'][number];
export type BatchMarkPrintedResult = components['schemas']['BatchMarkPrintedResult'];
export type BatchMarkPrintedOrder = BatchMarkPrintedResult['orders'][number];
export type OrderProductionStatus = components['schemas']['OrderProduction']['status'];

/** How a print's completion summary buckets each touched order. */
export type OrderCompletionGroup =
  | 'completed'
  | 'reprint'
  | 'unverified'
  | 'pending'
  | 'attention'
  | 'other';

/**
 * Bucket an order's post-print production status for the "what did this sheet complete?" summary:
 *  completed — all DTF work done (PRINTED/SHIPPED); pending — still open/in-production (e.g. waiting
 *  on another sheet); attention — a line is unmapped/blocked/unpaid; other — cancelled / no DTF work.
 *
 * ⚠️ STATUS ONLY, and deliberately NOT EXPORTED. `completed` here means "the line ledger says done",
 * which is not the same as shippable — the ledger cannot see a reprint. Keeping this module-private makes
 * {@link orderCompletionBucket} the only way in, so a future surface CANNOT key a done-claim on the raw
 * status without editing this file: compiler-enforced rather than comment-enforced. That distinction is
 * the whole reason the mark-printed dialog was missed the first time (§8.25).
 */
function orderCompletionGroup(status: OrderProductionStatus): OrderCompletionGroup {
  switch (status) {
    case 'PRINTED':
    case 'SHIPPED':
      return 'completed';
    case 'IN_PRODUCTION':
    case 'NOT_STARTED':
      return 'pending';
    case 'NEEDS_ACTION':
      return 'attention';
    default:
      return 'other';
  }
}

/**
 * Per-order outstanding-reprint state for the completion summary. `checked: false` means the lookup has
 * not answered — in flight OR failed — and `outstanding` is then meaningless.
 */
export type OrderReprintState = { checked: boolean; outstanding: number };

/**
 * The completion bucket a SHIP-FACING summary may use (decision D4 / §8.25).
 *
 * The mark-printed dialog is the third surface that made a done-claim straight from the rollup status,
 * and the one the D4 fix originally missed — found by adversarial review, by two lenses independently.
 * It matters more than its size suggests: it is shown at the exact moment the floor collates and bags a
 * sheet's orders, and a reissue never repoints the source line's `batchId`, so the rollup says PRINTED
 * while the replacement garment is still on the press.
 *
 * Only the `completed` claim is gated — every other bucket already reads as unfinished. Unknown is not
 * safe: an unanswered lookup demotes to `unverified` rather than passing as clean, mirroring
 * `orderHeadline` on the order card.
 */
export function orderCompletionBucket(
  status: OrderProductionStatus,
  reprint: OrderReprintState,
): OrderCompletionGroup {
  const base = orderCompletionGroup(status);
  if (base !== 'completed') return base;
  if (!reprint.checked) return 'unverified';
  return reprint.outstanding > 0 ? 'reprint' : 'completed';
}

/** Not knowing is the safe default — see `bucketOrdersByCompletion`'s length-mismatch guard. */
const REPRINT_UNKNOWN: OrderReprintState = { checked: false, outstanding: 0 };

/**
 * Pair each order with its ship-facing bucket, zipping `reprintStates` POSITIONALLY: `reprintStates[i]`
 * must be the state for `orders[i]`, which the caller guarantees by `.map`-ing over the query array the
 * orders built.
 *
 * ⚠️ A short `reprintStates` falls back to UNKNOWN per missing entry rather than to a clean check. The
 * hazard being closed is quiet mispairing: if the two arrays ever drift, an order must not inherit a
 * neighbour's all-clear, and the tail must not read as verified because nobody looked.
 */
export function bucketOrdersByCompletion<
  T extends { production: { status: OrderProductionStatus } },
>(
  orders: readonly T[],
  reprintStates: readonly OrderReprintState[],
): Array<{ order: T; bucket: OrderCompletionGroup }> {
  return orders.map((order, i) => ({
    order,
    bucket: orderCompletionBucket(order.production.status, reprintStates[i] ?? REPRINT_UNKNOWN),
  }));
}

export type BatchStatusMeta = { label: string; className: string };

/** Colour is meaning: sky = working, green = ready to print, red = failed, neutral = draft/shipped. */
export const BATCH_STATUS_META: Record<BatchStatus, BatchStatusMeta> = {
  DRAFT: { label: 'Draft', className: 'border-border bg-muted text-muted-foreground' },
  COMPOSITING: {
    label: 'Compositing',
    className:
      'border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300',
  },
  READY: {
    label: 'Ready',
    className:
      'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
  },
  PRINTED: {
    label: 'Printed',
    className:
      'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300',
  },
  FAILED: {
    label: 'Failed',
    className: 'border-destructive/40 bg-destructive/10 text-destructive',
  },
  SHIPPED: { label: 'Shipped', className: 'border-border bg-muted text-muted-foreground' },
};

/** Poll a batch's detail while it is compositing; stop once it settles (READY/FAILED). */
export function isCompositing(status: BatchStatus | undefined): boolean {
  return status === 'COMPOSITING';
}

/**
 * `refetchInterval` for the batches LIST query: poll while ANY row is mid-composite, then stop.
 *
 * The compositor is an async worker in FspShopifyStack, so nothing in the browser session learns when
 * a sheet lands. Only the batch *detail* query polled — and Build navigates to the list with nothing
 * selected, so a row read "Compositing" until it was clicked or the page reloaded.
 *
 * Extracted here (rather than left inline) purely so the SHAPE is pinned by a test: the payload array
 * is `items`, and a selector written against a non-existent `batches` key is a silent no-op that reads
 * as though it works.
 */
export function compositingPollInterval(
  data: { items?: { status: BatchStatus }[] } | undefined,
): number | false {
  return (data?.items ?? []).some((b) => isCompositing(b.status)) ? 2500 : false;
}

/** Flatten an infinite-query page set into the `{items}` shape `compositingPollInterval` reads —
 *  the batches list is cursor-paginated with a manual "Load older" (production-records R0). Takes
 *  `unknown` because TanStack's refetchInterval callback sees the cache before TData inference
 *  resolves; the page shape is narrowed structurally here, once, instead of casting at call sites. */
export function flattenBatchPages(data: unknown): { items: { status: BatchStatus }[] } {
  const pages =
    data && typeof data === 'object' && 'pages' in data && Array.isArray(data.pages)
      ? // Runtime shape is this query's own page array (BatchListResponse pages) — safe to assert.
        (data.pages as { items?: { status: BatchStatus }[] }[])
      : [];
  return { items: pages.flatMap((p) => p.items ?? []) };
}

/**
 * Find-by-number over the LOADED sheets (production-records R0): "#44"/"44" matches seq 44 exactly;
 * a non-numeric term prefix-matches the batch id. Empty term = everything (the normal list).
 */
export function filterBatchesBySeq<T extends Pick<BatchSummary, 'seq' | 'batchId'>>(
  batches: readonly T[],
  term: string,
): T[] {
  const q = term.trim().replace(/^#/, '');
  if (!q) return [...batches];
  if (/^\d+$/.test(q)) return batches.filter((b) => b.seq === Number(q));
  return batches.filter((b) => b.batchId.startsWith(q));
}

// ── The workbench rail (gang-sheets redesign v1, PR-1) ──

/**
 * The statuses that make up the rail's "To print" section — the WORKING SET. Fetched one
 * `GET /batches?status=` each, so the set is complete however long history gets: the unfiltered list
 * sorts by `STATUS#<status>` as a string, descending, which puts FAILED / DRAFT / COMPOSITING BELOW every
 * PRINTED row (page 2 once there are more than 100 batches). SHIPPED is deliberately absent — nothing
 * writes it (gang-sheet-production-plan § "as-built").
 */
export const WORKING_STATUSES = ['FAILED', 'READY', 'COMPOSITING', 'DRAFT'] as const;
export type WorkingStatus = (typeof WORKING_STATUSES)[number];

export function isWorkingStatus(status: BatchStatus): status is WorkingStatus {
  return (WORKING_STATUSES as readonly BatchStatus[]).includes(status);
}

/**
 * Rank inside "To print" (plan D3): Failed first (its lines are claimed but have no film — it needs a
 * decision), then Ready MID-JOB (some sheets already ticked — finish what is on the printer before
 * starting another), then Ready untouched oldest-first (FIFO — print in the order built), then
 * Compositing, then Draft.
 *
 * Mid-job reads `printedSheetCount`, which the server projects onto the summary since PR-5; a summary
 * from an older API carries none and ranks as untouched, which is what it was before.
 */
const WORKING_RANK: Record<WorkingStatus, number> = {
  FAILED: 0,
  READY: 1,
  COMPOSITING: 2,
  DRAFT: 3,
};

/** A Ready sheet the floor has started on — at least one physical sheet ticked as printed. */
export function midJob(b: Pick<BatchSummary, 'status' | 'printedSheetCount'>): boolean {
  return b.status === 'READY' && (b.printedSheetCount ?? 0) > 0;
}

export function workingSetOrder<
  T extends Pick<BatchSummary, 'status' | 'createdAt' | 'printedSheetCount'>,
>(batches: readonly T[]): T[] {
  return batches
    .filter((b): b is T & { status: WorkingStatus } => isWorkingStatus(b.status))
    .sort(
      (a, b) =>
        WORKING_RANK[a.status] - WORKING_RANK[b.status] ||
        Number(midJob(b)) - Number(midJob(a)) ||
        a.createdAt.localeCompare(b.createdAt) ||
        // Deterministic for two batches built in the same millisecond (a test fixture, if nothing else).
        (a as { batchId?: string }).batchId?.localeCompare(
          (b as { batchId?: string }).batchId ?? '',
        ) ||
        0,
    );
}

/**
 * Which sheet the workbench opens when the URL names none (plan D2): the sheet last opened this
 * session, if it is still on the rail → else the first Ready row → else the first row of the working
 * set → else nothing (the empty state). `lastOpened` is checked against BOTH sections so a printed sheet
 * the operator was reading survives a reload.
 */
export function pickDefaultBatch<
  T extends Pick<BatchSummary, 'batchId' | 'status' | 'createdAt' | 'printedSheetCount'>,
>(working: readonly T[], printed: readonly T[], lastOpened: string | null): string | null {
  if (lastOpened && [...working, ...printed].some((b) => b.batchId === lastOpened))
    return lastOpened;
  const ordered = workingSetOrder(working);
  return ordered.find((b) => b.status === 'READY')?.batchId ?? ordered[0]?.batchId ?? null;
}

/**
 * The legacy `?batch=<id>` deep link (the order card and the production board still send it): the id to
 * redirect to `/batches/$batchId`, or null when the search carries none. Pure so the router's
 * `beforeLoad` stays a one-liner and the rule is pinned by a test.
 */
export function legacyBatchRedirect(search: Record<string, unknown>): string | null {
  const v = search.batch;
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/**
 * The rail's Find box: a number PREFIX ("13" → #130…#139, "#136" → #136 exactly as a prefix of one),
 * else a brand substring ("obs" → Team Obs) or a batch-id prefix for a pre-counter batch. Empty =
 * everything. Replaces {@link filterBatchesBySeq}'s exact-number match on this surface: the rail has
 * two sections to narrow at once, and a brand is how the floor thinks of a sheet.
 */
export function filterRail<
  T extends Pick<BatchSummary, 'seq' | 'batchId' | 'brandLabel' | 'storeIds'>,
>(batches: readonly T[], term: string): T[] {
  const q = term.trim().replace(/^#/, '').toLowerCase();
  if (!q) return [...batches];
  if (/^\d+$/.test(q)) return batches.filter((b) => b.seq != null && String(b.seq).startsWith(q));
  return batches.filter(
    (b) =>
      (b.brandLabel ?? b.storeIds.join(', ')).toLowerCase().includes(q) ||
      b.batchId.toLowerCase().startsWith(q),
  );
}

/**
 * The Find term as ONE exact sheet number ("#44" / "44" → 44), else null — what the server lookup
 * (`GET /batches?seq=`) takes when the term matches nothing loaded. A brand, an empty box or "0" is
 * not a number to look up.
 */
export function seqFindNumber(term: string): number | null {
  const q = term.trim().replace(/^#/, '');
  return /^\d+$/.test(q) && Number(q) > 0 ? Number(q) : null;
}

/** What the card's "Next:" line needs — the batch's state plus the sheet progress the card already has. */
export type NextStepInput = {
  status: BatchStatus;
  sheetCount: number;
  printedSheetCount: number;
  /** A mixed batch whose screen-print work is not yet pressed — Mark printed is gated on it. */
  screenPrintPending: boolean;
  /**
   * Pulled-blanks v2: whether every pull-list row of this sheet is ticked. `false` appends the pull
   * station's step to the READY sentences; `undefined` (a caller that does not know) leaves them as
   * they were.
   */
  pullDone?: boolean;
};

/**
 * "Next: …" — the one sentence under the action bar that says what to do now, derived from state so a
 * new operator does not have to infer the workflow from button colours. Null when the banner above
 * already says it (compositing) or there is nothing to do (shipped).
 *
 * The rule is the same one that picks the PRIMARY button (`primaryAction`): the two are computed from
 * the same input so the sentence and the red button cannot disagree.
 */
export function nextStep(b: NextStepInput): string | null {
  switch (b.status) {
    case 'DRAFT':
      return 'Next: generate the gang sheet — nothing prints until the film is composited.';
    case 'FAILED':
      return 'Next: fix what the failure names, then Retry — or Delete to return its lines to the Orders queue.';
    case 'READY': {
      // The pull station's step rides on every READY sentence until the rows are ticked (D18: nothing is
      // set by printing or opening; the tick is the record) — placed where the floor does it: after the
      // films print, before the press and before the close.
      const pull = b.pullDone === false;
      if (b.screenPrintPending) {
        return pull
          ? 'Next: tick the pull list once the stack is sorted, press the screen-print garments at the press station (or “Mark all pressed” below), then close.'
          : 'Next: press the screen-print garments at the press station (or “Mark all pressed” below), then close.';
      }
      if (b.sheetCount > 0 && b.printedSheetCount < b.sheetCount) {
        const sheets = remainingSheetsLabel(b.sheetCount, b.printedSheetCount);
        return pull
          ? `Next: print ${sheets}, tick each as it comes off the printer, then tick the pull list once the stack is sorted.`
          : `Next: print ${sheets}, tick each as it comes off the printer.`;
      }
      return pull
        ? 'Next: labels for the cutting bench, tick the pull list once the stack is sorted, then Mark printed to close.'
        : 'Next: labels for the cutting bench, then Mark printed to close.';
    }
    case 'PRINTED':
      return 'Closed — Undo printed reopens the batch and its lines.';
    default:
      return null;
  }
}

/**
 * "sheet 3" / "sheets 3–5" for the sheets still to print, assuming they are ticked in order — the
 * common case. Out-of-order ticks are not known here (only the count is), so the label names the
 * TAIL of the run; the Sheets card shows the exact ticks.
 */
export function remainingSheetsLabel(sheetCount: number, printedSheetCount: number): string {
  const first = printedSheetCount + 1;
  if (first >= sheetCount) return `sheet ${sheetCount}`;
  return `sheets ${first}–${sheetCount}`;
}

/** Which control is RED on the card — exactly one per state (plan D6). */
export type PrimaryAction = 'generate' | 'retry' | 'download' | 'markPrinted' | null;

export function primaryAction(b: NextStepInput): PrimaryAction {
  switch (b.status) {
    case 'DRAFT':
      return 'generate';
    case 'FAILED':
      return 'retry';
    case 'READY':
      if (b.screenPrintPending) return 'download';
      if (b.sheetCount > 0 && b.printedSheetCount < b.sheetCount) return 'download';
      return 'markPrinted';
    default:
      return null;
  }
}

/** sessionStorage key for "the sheet last opened this session" — per viewer, per tab; never shared. */
export const LAST_OPENED_BATCH_KEY = 'fsp.batches.lastOpened';

/**
 * Operator-facing batch label. A batch is a whole gang-sheet job that composites into one OR MORE
 * physical sheets, so it's labelled "Batch #12" (NOT "Sheet #12" — that collided with the physical
 * "Sheet 1..N" inside it). Batches created before the counter existed have no `seq` and fall back to
 * the short id ("Batch c2dfd8") so nothing is blank.
 */
export function batchLabel(batch: Pick<BatchSummary, 'seq' | 'batchId'>): string {
  return batch.seq != null ? `Batch #${batch.seq}` : `Batch ${batch.batchId.slice(0, 8)}`;
}

/** The rail's short form of {@link batchLabel} — "#12" beside a brand and a status pill, where "Batch"
 *  is implied by the page. Same fallback for a pre-counter batch. */
export function batchShortLabel(batch: Pick<BatchSummary, 'seq' | 'batchId'>): string {
  return batch.seq != null ? `#${batch.seq}` : batch.batchId.slice(0, 8);
}

/**
 * The "Reprint of …" badge text, naming EVERY source sheet a cross-source reprint draws from.
 *
 * `reissueOf` carries the primary (lowest-seq) source and stays the single-source answer for every
 * correctness reader — they are all truthiness checks. But naming only the primary on a sheet that
 * merges three origins tells the floor to look at the wrong one, and a queue-flushed sheet is
 * cross-source BY CONSTRUCTION: merging is the entire point of the queue. `reissueSources` is
 * populated only when there really is more than one, so a single-source reissue reads exactly as it
 * always did.
 */
export function reissueLabel(
  batch: Pick<BatchSummary, 'reissueOf' | 'reissueOfSeq' | 'reissueSources'>,
): string | null {
  if (!batch.reissueOf) return null;
  const name = (b: { batchId: string; seq?: number }) =>
    b.seq != null ? `#${b.seq}` : b.batchId.slice(0, 8);
  const sources = batch.reissueSources ?? [];
  if (sources.length > 1) return `Reprint of ${sources.map(name).join(', ')}`;
  return `Reprint of ${name({ batchId: batch.reissueOf, seq: batch.reissueOfSeq })}`;
}

/** Canonical print resolution — 1 print inch = 300 px (matches the engine). */
export const PRINT_DPI = 300;

/**
 * Largest sheet canvas the compositor can render at its current memory (px). MUST mirror
 * gang-sheet-engine's `MAX_SHEET_CANVAS_PX` (294 MP — see the calibration note there) — the server
 * clamps builds authoritatively; this only bounds the Build dialog's length input so the operator
 * isn't offered a length that the server would silently shorten. Raise both together.
 */
export const MAX_SHEET_CANVAS_PX = 294_000_000;

/** Longest sheet (whole inches) that renders within budget for a roll of `rollWidthIn` at 300 DPI. */
export function memorySafeMaxLengthIn(rollWidthIn: number): number {
  return Math.max(1, Math.floor(MAX_SHEET_CANVAS_PX / (rollWidthIn * PRINT_DPI * PRINT_DPI)));
}

/**
 * Round an inch measurement for display: at most 2 decimals, trailing zeros trimmed.
 * `10.003333…` → "10", `1.586666…` → "1.59", `12.866666…` → "12.87", `17.5` → "17.5".
 */
export function formatInches(n: number): string {
  return Number(n.toFixed(2)).toString();
}

// ── Preview fit (gang-sheets redesign v1, PR-2) ──

/**
 * The FLOOR of the compositor's preview resolution across the roll-width axis: 800px for a sheet longer
 * than it is wide (≈23 px/in on a 35″ roll), and since PR-4 1,600px for one no longer than it is wide
 * (`previewWidthPx` in `png-sheet-encoder.ts`); sheets composited before PR-4 are 800 either way. The
 * cap below is computed from this floor, so it is conservative for a 1,600px preview — which simply
 * draws crisper — and never lies for an 800px one. The cap binds only at Full size, where it holds the
 * 800px case at exactly 2×.
 */
export const PREVIEW_NATIVE_WIDTH_PX = 800;
/** Never draw the preview above this multiple of its native resolution — past it the film is blur. */
export const PREVIEW_MAX_UPSCALE = 2;
/** The film panel never grows taller than this, whatever the sheet's shape. */
export const PREVIEW_BOX_MAX_HEIGHT_PX = 560;

/** The film panel's height for a viewport: 60% of it, capped, never below a readable floor. */
export function previewBoxHeight(viewportHeightPx: number): number {
  return Math.max(240, Math.min(Math.round(viewportHeightPx * 0.6), PREVIEW_BOX_MAX_HEIGHT_PX));
}

export type PreviewFit = {
  /** True when the film is turned 90° so its LENGTH runs across the panel. */
  rotated: boolean;
  pxPerIn: number;
  /** Rendered footprint, px — the box the (possibly rotated) image fills exactly. */
  width: number;
  height: number;
  widthIn: number;
  lengthIn: number;
};

/**
 * "Rotate only when it buys pixels." The film is `widthIn` across the roll and `lengthIn` long. Fit it
 * into the panel both ways — as printed (width across) and turned 90° (length across) — and draw
 * whichever gives more pixels per inch, capped at {@link PREVIEW_MAX_UPSCALE}× the PNG's native
 * resolution so nothing is blown up blurry.
 *
 * Replaces the rule that rotated EVERY sheet and sized the box to the rotated aspect with no height
 * cap: the nester trims a sheet to its content (`nest.ts`), so a one-line reprint is a short WIDE strip
 * — 52 of the 194 live sheets are wider than long — and turning it on end drew a 12.8″ sheet as a
 * 900 × 2,460px box and a 2.87″ strip as 900 × 10,988px (measured on the live page, 2026-09-27). A
 * long sheet still lands landscape exactly as before.
 */
export function previewFit(
  sheet: { widthPx: number; heightPx: number },
  box: { width: number; height: number },
): PreviewFit {
  const widthIn = sheet.widthPx / PRINT_DPI;
  const lengthIn = sheet.heightPx / PRINT_DPI;
  // Compare the CAPPED scales: once the resolution ceiling binds, turning buys nothing, and a tie goes
  // to "as printed" (the full-size view of a short sheet must not stand it on end).
  const cap = (PREVIEW_NATIVE_WIDTH_PX / widthIn) * PREVIEW_MAX_UPSCALE;
  const turned = Math.min(box.width / lengthIn, box.height / widthIn, cap);
  const flat = Math.min(box.width / widthIn, box.height / lengthIn, cap);
  const rotated = turned > flat;
  const pxPerIn = rotated ? turned : flat;
  return {
    rotated,
    pxPerIn,
    width: (rotated ? lengthIn : widthIn) * pxPerIn,
    height: (rotated ? widthIn : lengthIn) * pxPerIn,
    widthIn,
    lengthIn,
  };
}

/**
 * Tick marks for the inch ruler under the film, along the axis that runs ACROSS the panel: a labelled
 * major tick every 10″ (5″ on a sheet of 20″ or less), a minor tick between. `left` is px from the
 * ruler's start for a ruler `px` wide.
 */
export function rulerTicks(inches: number, px: number): Array<{ left: number; label?: string }> {
  if (!(inches > 0) || !(px > 0)) return [];
  const major = inches <= 20 ? 5 : 10;
  const minor = inches <= 20 ? 1 : 5;
  const ticks: Array<{ left: number; label?: string }> = [];
  for (let i = 0; i <= inches + 1e-9; i += minor) {
    const left = (i / inches) * px;
    ticks.push(i % major === 0 ? { left, label: `${i}″` } : { left });
  }
  return ticks;
}

/** A composited sheet's physical size, e.g. `34 × 84″`, from its pixel dims at 300 DPI. */
export function sheetSizeLabel(s: { widthPx: number; heightPx: number }): string {
  return `${formatInches(s.widthPx / PRINT_DPI)} × ${formatInches(s.heightPx / PRINT_DPI)}″`;
}

export type BatchItemsSummary = { lineCount: number; pieceCount: number; designCount: number };

export function summarizeBatchItems(items: readonly BatchItemSummary[]): BatchItemsSummary {
  const designs = new Set<string>();
  // One order line expands to one item PER placement (catalog-mapping v1), so items sharing a
  // (store, order, line) are the same line — count DISTINCT lines to match the server's batch.lineCount.
  const lines = new Set<string>();
  let pieceCount = 0;
  for (const it of items) {
    designs.add(it.designId);
    lines.add(`${it.storeId}#${it.orderId}#${it.lineId}`);
    pieceCount += it.quantity;
  }
  return { lineCount: lines.size, pieceCount, designCount: designs.size };
}

// ── Pick-list manifest helpers ──

export type ManifestSheetGroup = { sheetIndex: number; transfers: BatchManifestTransfer[] };

/**
 * Group a manifest's transfers by sheet, in canonical order — sheets ascending, and within each
 * sheet the transfers in their global `index` order (reading order on the film). This is what the
 * printable pick list renders: one section per physical sheet the operator cuts.
 */
export function groupTransfersBySheet(m: {
  transfers: readonly BatchManifestTransfer[];
}): ManifestSheetGroup[] {
  const bySheet = new Map<number, BatchManifestTransfer[]>();
  // Off-sheet (pulled-from-stock) transfers aren't on any physical sheet to cut — skip them here.
  for (const t of [...m.transfers].filter((t) => !t.fromStock).sort((a, b) => a.index - b.index)) {
    const arr = bySheet.get(t.sheetIndex);
    if (arr) arr.push(t);
    else bySheet.set(t.sheetIndex, [t]);
  }
  return [...bySheet.keys()]
    .sort((a, b) => a - b)
    .map((sheetIndex) => ({ sheetIndex, transfers: bySheet.get(sheetIndex) ?? [] }));
}

export type BlankPull = {
  key: string;
  /** Bare S&S sku (the `BLANK#` prefix stripped), when the transfer resolved a blank — for cross-checking. */
  ssSku?: string;
  /** Human blank label — canonical "Tultex 602" (from `GET /inventory`), else the Shopify product title. */
  label: string;
  /** Colour · size — canonical "Black · L" (from inventory), else the Shopify variant title, else empty. */
  variant: string;
  /** Coarse size band (adult/youth) — the last-resort sub-label when no colour/size is known. */
  sizeBand: string;
  /** Distinct Shopify product titles that resolve to this blank — >1 when several collapse into one row. */
  titles: string[];
  /** DISTINCT garments (blanks) to pull — NOT transfers. A front+back garment is 2 transfers but 1 blank. */
  qty: number;
};

const BLANK_PREFIX = 'BLANK#';
/** A transfer's `resolvedBlankSku` is `BLANK#<ssSku>`; the inventory name map keys on the bare `<ssSku>`. */
function bareSsSku(resolvedBlankSku: string): string {
  return resolvedBlankSku.startsWith(BLANK_PREFIX)
    ? resolvedBlankSku.slice(BLANK_PREFIX.length)
    : resolvedBlankSku;
}

/**
 * Aggregate the manifest into a "blanks to pull" list — one row per physical blank with the number of
 * GARMENTS to gather. Counts distinct garments (a garment = one copy of one line), NOT transfers, so a
 * front+back print counts its blank once, and skips cancelled / refunded-after-batch copies (they're
 * not produced).
 *
 * `nameBySku` (bare ssSku → names, from `GET /inventory`) supplies the human blank label so the floor
 * reads "Tultex 602 · Black · L" instead of the opaque `BLANK#<sku>`. A transfer whose blank isn't in
 * the map — or that never resolved a blank — falls back to the Shopify product title + variant. When a
 * blank resolves, rows collapse by its (colour+size-specific) sku, so different Shopify products on the
 * same physical blank merge into one shelf-pull row. Sorted by label, colour, then size.
 */
export function blanksToPull(
  m: { transfers: readonly BatchManifestTransfer[] },
  nameBySku: ReadonlyMap<string, BlankName> = new Map(),
  /**
   * The batch's screen-print placements. ⚠️ REQUIRED IN PRACTICE for a FILMLESS run: it has zero
   * transfers, so without this the printable pull list renders EMPTY and tells the floor to pull
   * nothing for a run of 40 shirts. Under-pull is the dangerous direction — it strands a presser with
   * art and no shirt. Defaults to `[]` so every existing caller is unchanged.
   */
  screenPrintWork: readonly ScreenPrintWorkItem[] = [],
): BlankPull[] {
  type Internal = { row: BlankPull; colorName: string; sizeName: string; garments: Set<string> };
  const groups = new Map<string, Internal>();
  for (const t of m.transfers) {
    // A cancelled-after-batch line — or a copy refunded/removed after batching — must not be
    // produced, so don't pull a blank for it either.
    if (t.lineCancelled || t.lineQtyDropped) continue;
    // A TRANSFER-ONLY reprint presses onto the shirt already out on the floor: mark-printed draws no
    // replacement blank for it, so listing one to pull walks real stock off the shelf that the ledger
    // never decrements. Latent before per-film reprints (a whole keep-the-blank sheet was unusual);
    // routine now that tearing one film is the normal cutting-bench report.
    if (t.reissueTransferOnly) continue;
    const sku = t.resolvedBlankSku ? bareSsSku(t.resolvedBlankSku) : undefined;
    const name = sku ? nameBySku.get(sku) : undefined;
    // Collapse by the physical blank when its sku is known (the sku is colour+size specific); else the
    // product + variant + band can't be merged further.
    const key = sku ?? `t:${t.title}##${t.variantTitle ?? ''}##${t.sizeBand}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        row: {
          key,
          ssSku: sku,
          // A resolved-but-nameless inventory row would otherwise label the row with the bare sku; the
          // helper falls back to the transfer's readable Shopify title/variant in that case.
          label: pullBlankLabel(name, t.title),
          variant: pullBlankVariant(name, t.variantTitle),
          sizeBand: t.sizeBand,
          titles: [],
          qty: 0,
        },
        colorName: name?.colorName ?? '',
        sizeName: name?.sizeName ?? '',
        garments: new Set(),
      };
      groups.set(key, g);
    }
    // A garment is one COPY of one line — every placement of that copy rides the same blank.
    g.garments.add(`${t.storeId}#${t.orderId}#${t.lineId}#${t.copyIndex}`);
    if (!g.row.titles.includes(t.title)) g.row.titles.push(t.title);
  }
  // ── Screen-print placements ────────────────────────────────────────────────────────────────────
  // ⚠️ ONLY for lines with NO transfer at all. A MIXED line is already counted above by its film
  // placements, and adding its screen-print entries too would pull a second shirt for one garment.
  //
  // ⚠️ The two sides COUNT DIFFERENTLY, which is the whole subtlety. A transfer row is per-COPY (it
  // carries `copyIndex`, so the Set counts garments directly); a `ScreenPrintWorkItem` is per-PLACEMENT
  // and carries `quantity` instead. So a front+back qty-2 line is TWO entries each saying 2 — and the
  // two naive folds give 1 (dedupe only, an under-pull) or 4 (sum both, an over-pull). Correct is:
  // dedupe by line, then synthesize one garment key per copy of that line — 2.
  const linesWithTransfers = new Set(
    m.transfers.map((t) => `${t.storeId}#${t.orderId}#${t.lineId}`),
  );
  const spByLine = new Map<string, ScreenPrintWorkItem>();
  for (const w of screenPrintWork) {
    const lineKey = `${w.storeId}#${w.orderId}#${w.lineId}`;
    if (linesWithTransfers.has(lineKey)) continue;
    if (!spByLine.has(lineKey)) spByLine.set(lineKey, w);
  }
  for (const [lineKey, w] of spByLine) {
    const sku = w.resolvedBlankSku ? bareSsSku(w.resolvedBlankSku) : undefined;
    const name = sku ? nameBySku.get(sku) : undefined;
    const key = sku ?? `t:${w.title}##${w.variantTitle ?? ''}##${w.sizeBand}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        row: {
          key,
          ssSku: sku,
          label: pullBlankLabel(name, w.title),
          variant: pullBlankVariant(name, w.variantTitle),
          sizeBand: w.sizeBand,
          titles: [],
          qty: 0,
        },
        colorName: name?.colorName ?? '',
        sizeName: name?.sizeName ?? '',
        garments: new Set(),
      };
      groups.set(key, g);
    }
    // One synthetic garment key per copy, so this shares the Set the transfers side counts.
    for (let copy = 0; copy < w.quantity; copy += 1) g.garments.add(`${lineKey}#sp${copy}`);
    if (!g.row.titles.includes(w.title)) g.row.titles.push(w.title);
  }
  // ⚠️ KNOWN GAP, and it errs toward OVER-pull (the safe direction): the transfers side skips a
  // cancelled / partially-refunded line, but `ScreenPrintWorkItem` carries no line state, so a filmless
  // line cancelled after its build is still listed. Pulling a shirt you did not need beats stranding a
  // presser. Fixing it means carrying the line's state onto the screen-print entry.
  return [...groups.values()]
    .map((g) => ({ g, qty: g.garments.size }))
    .sort(
      (a, b) =>
        a.g.row.label.localeCompare(b.g.row.label) ||
        a.g.colorName.localeCompare(b.g.colorName) ||
        sizeRank(a.g.sizeName) - sizeRank(b.g.sizeName) ||
        a.g.row.variant.localeCompare(b.g.row.variant),
    )
    .map(({ g, qty }) => ({ ...g.row, qty }));
}

/**
 * One garment ticked in the cut list's reprint picker — a PHYSICAL SHIRT, not a line and not a
 * placement. Every placement of a garment re-presses onto one blank, so ticking any of its transfer
 * rows selects the whole garment.
 */
export type ReprintSelection = {
  storeId: string;
  orderId: string;
  lineId: string;
  /**
   * 0-based copy index from the manifest.
   *
   * REQUIRED, unlike the reissue selection it replaces. The reprint queue's identity is
   * `(store, order, line, owedRank)` with a 1-BASED rank, so a garment with no copy index cannot be
   * turned into an add at all — it would have to guess, and guessing names a different shirt: the
   * ruined one never gets reprinted and a blank is drawn for one that was fine. Making it
   * non-optional moves that from a runtime bug to a compile error.
   */
  copyIndex: number;
  /**
   * The RUINED transfers — which of this garment's prints the reporter ticked, sorted. At the cutting
   * bench a torn back leaves the front film perfectly good, so reprinting both wastes a film and a
   * press cycle.
   *
   * Sent as `ruinedLocations` ONLY when the garment is keep-the-blank. A garment drawing a fresh shirt
   * reprints every print regardless — you cannot press a bare front — so a subset on it is meaningless
   * and is never sent.
   */
  locations: string[];
  /** Every print this garment carries on the sheet, from the manifest. Display only: it is what lets
   *  the panel say "reprints ALL 2 prints" without a round trip to the server. */
  allLocations: string[];
};

/**
 * The selection key, shared by the checkbox state and the keep-the-blank state so the two cannot
 * drift. Includes the copy index and NOT the location: ticking a garment's second transfer row is
 * the same garment.
 */
export function garmentKey(
  r: Pick<ReprintSelection, 'storeId' | 'orderId' | 'lineId' | 'copyIndex'>,
): string {
  return `${r.storeId}#${r.orderId}#${r.lineId}#${r.copyIndex}`;
}

/**
 * Tick or untick ONE transfer of one garment, returning both pieces of picker state.
 *
 * Pure and returned together on purpose: the selection and the keep-the-blank answer are keyed the
 * same way and have to move in lockstep, and the rules below are the ones worth a test rather than a
 * click-through.
 *
 *  · A garment's films union — ticking the back after the front gives BOTH, never the last one.
 *  · Unticking the last film deselects the garment AND drops its keep-the-blank answer, so a later
 *    re-tick starts from the station default instead of silently inheriting a stale answer.
 *  · `keepBlankByDefault` is the station's policy. The cutting bench passes `true`: cutting happens
 *    before pressing, so the shirt is not in the cutter's hands and a torn film cannot have damaged
 *    it. Everywhere else the default stays off, which is the direction that draws a replacement.
 */
export function toggleTransferSelection(
  state: {
    selected: ReadonlyMap<string, ReprintSelection>;
    keepBlank: ReadonlySet<string>;
  },
  key: string,
  ref: ReprintSelection,
  opts: { keepBlankByDefault: boolean },
): { selected: Map<string, ReprintSelection>; keepBlank: Set<string> } {
  const selected = new Map(state.selected);
  const keepBlank = new Set(state.keepBlank);
  const clicked = ref.locations[0];
  const cur = selected.get(key);
  if (!cur) {
    selected.set(key, ref);
    if (opts.keepBlankByDefault) keepBlank.add(key);
    return { selected, keepBlank };
  }
  const locations = cur.locations.includes(clicked as string)
    ? cur.locations.filter((l) => l !== clicked)
    : [...new Set([...cur.locations, ...ref.locations])].sort();
  if (locations.length === 0) {
    selected.delete(key);
    keepBlank.delete(key);
  } else {
    selected.set(key, { ...cur, locations });
  }
  return { selected, keepBlank };
}

/** Body for `POST /batches/reprint-cart/entries`, straight off the generated contract. */
export type ReprintQueueAddBody = components['schemas']['ReprintQueueAddBody'];

/**
 * One queue-add body per selected garment, oldest-line-first and deterministic.
 *
 * Per GARMENT, not per line — that is the whole difference from the reissue body this replaces, and
 * it is a money difference. `transferOnly` is stored per queue row, and the storage layer treats
 * `true` as a one-way downgrade; a line with one torn transfer (blank fine) and one scorched garment
 * needs one row true and one false. Collapsing the keep-the-blank decision to the line, as the
 * reissue body forced, sends true for both — the flush then ANDs to true, mark-printed filters the
 * item out of the blank consumables, and the scorched garment gets no replacement shirt at all.
 *
 * `keepBlank` holds `garmentKey`s. Absent = the blank is ruined, which is the safe default: it draws
 * a replacement rather than assuming one is not needed.
 */
export function queueAddsForSelection(
  selection: readonly ReprintSelection[],
  opts: {
    sourceBatchId: string;
    keepBlank: ReadonlySet<string>;
    reason?: ReprintQueueAddBody['reason'];
    station?: ReprintQueueAddBody['station'];
  },
): Array<{ key: string; garment: ReprintSelection; body: ReprintQueueAddBody }> {
  const byGarment = new Map<string, ReprintSelection>();
  // Collapse to ONE add per garment, UNIONING the ruined locations. First-wins would silently drop the
  // second ticked film — the garment would come back with only one of its two torn transfers reprinted,
  // which is the exact ship-short this whole path is built to avoid. (The picker's own state already
  // holds one entry per garment, so this is defensive; the union is what makes it safe rather than
  // lucky.)
  for (const r of selection) {
    const key = garmentKey(r);
    const prev = byGarment.get(key);
    if (!prev) {
      byGarment.set(key, r);
      continue;
    }
    byGarment.set(key, {
      ...prev,
      locations: [...new Set([...prev.locations, ...r.locations])].sort(),
      allLocations:
        prev.allLocations.length >= r.allLocations.length ? prev.allLocations : r.allLocations,
    });
  }
  return [...byGarment.values()]
    .sort(
      (a, b) =>
        a.storeId.localeCompare(b.storeId) ||
        a.orderId.localeCompare(b.orderId) ||
        a.lineId.localeCompare(b.lineId) ||
        a.copyIndex - b.copyIndex,
    )
    .map((r) => ({
      // Returned alongside the body so the caller can attribute each request's outcome to the
      // garment that produced it WITHOUT re-deriving the rank — the 0-based↔1-based conversion has
      // exactly one site on this surface, and a second one is how the two drift.
      key: garmentKey(r),
      garment: r,
      body: {
        storeId: r.storeId,
        orderId: r.orderId,
        lineId: r.lineId,
        // 0-based copy index → 1-based owed rank. The single conversion site.
        owedRank: r.copyIndex + 1,
        sourceBatchId: opts.sourceBatchId,
        reason: opts.reason ?? 'CUT_DAMAGE',
        station: opts.station ?? 'CUTTING',
        // ⚠️ THE TWO FIELDS SHIP TOGETHER OR NOT AT ALL. `ruinedLocations` only ever narrows the sheet
        // alongside `transferOnly: true`, and the server enforces that independently — but sending a
        // subset for a garment that is drawing a fresh blank would put a number in the confirm dialog
        // that the sheet then contradicts. One conditional, one site, so they cannot drift apart.
        ...(opts.keepBlank.has(garmentKey(r))
          ? {
              transferOnly: true,
              ...(r.locations.length > 0 ? { ruinedLocations: r.locations } : {}),
            }
          : {}),
        // Audit only — which transfer the reporter pointed at first. `ruinedLocations` above is the
        // field that decides what prints.
        ...(r.locations[0] ? { damagedLocation: r.locations[0] } : {}),
      },
    }));
}

/**
 * What stays ticked after a batch of queue adds.
 *
 * ⚠️ THE SHIP-SHORT GUARD. There is no bulk add, so N ticked garments are N requests and a PARTIAL
 * failure is the normal failure. A garment with no queue row has nothing blocking its order from the
 * ship lanes, so clearing the whole selection on settle — which is what the single-request reissue
 * this replaced did — hides the gap completely: the operator sees one banner, the ticks are gone,
 * and the box goes out a shirt short with nothing anywhere saying so. Only what actually LANDED is
 * dropped; a refused garment stays ticked, keeping its keep-the-blank answer, ready to retry.
 */
export function applyQueueAddOutcomes<T>(
  selected: ReadonlyMap<string, T>,
  keepBlank: ReadonlySet<string>,
  outcomes: readonly { key: string; ok: boolean }[],
): { selected: Map<string, T>; keepBlank: Set<string> } {
  const landed = new Set(outcomes.filter((o) => o.ok).map((o) => o.key));
  return {
    selected: new Map([...selected].filter(([k]) => !landed.has(k))),
    keepBlank: new Set([...keepBlank].filter((k) => !landed.has(k))),
  };
}

/** One print location on a garment, with the design placed there and its true (pre-rotation) size. */
export type ManifestGarmentPlacement = {
  location: string;
  designId: string;
  designName?: string;
  widthIn: number;
  heightIn: number;
  /** Pressed from a printed transfer PULLED FROM STOCK (off the sheet) rather than printed on it. */
  fromStock: boolean;
};

/** One garment (order line) the press station works, with every location it receives. */
export type ManifestGarmentGroup = {
  key: string;
  storeId: string;
  orderId: string;
  orderName: string;
  title: string;
  variantTitle?: string;
  sizeBand: string;
  resolvedBlankSku?: string;
  /** Physical shirts in this group — identical copies of the line collapse into a count. */
  quantity: number;
  lineCancelled: boolean;
  /** Copies of this garment refunded/removed from the order AFTER batching — on the film but NOT to
   *  be pressed (`quantity` still counts them; press `quantity - qtyDropped`). 0 when none / cancelled. */
  qtyDropped: number;
  /** Distinct print locations on this garment (front/back/left-chest…), sorted by location. */
  placements: ManifestGarmentPlacement[];
};

/**
 * Group a manifest's transfers by GARMENT (one order line) for the pressing station: one row per
 * shirt style, listing every location it gets (front/back/left-chest) with the design + true print
 * size. The assembly-side complement to the film-scan {@link groupTransfersBySheet}: that list is in
 * cut order (scattered across orders); this one collects each shirt's prints together so whoever
 * presses it sees the whole job at once. All copies of a line are identical (same locations, same
 * designs), so copies collapse into `quantity` — matching the garment count in {@link blanksToPull}.
 * `rot` is intentionally omitted: film orientation doesn't change how a cut transfer presses onto a
 * shirt. Sorted by order then garment so an order's shirts sit together for bagging.
 */
export function groupTransfersByGarment(m: {
  transfers: readonly BatchManifestTransfer[];
}): ManifestGarmentGroup[] {
  const groups = new Map<
    string,
    {
      row: ManifestGarmentGroup;
      copies: Set<number>;
      dropped: Set<number>;
      byLocation: Map<string, ManifestGarmentPlacement>;
    }
  >();
  for (const t of m.transfers) {
    const key = `${t.storeId}#${t.orderId}#${t.lineId}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        row: {
          key,
          storeId: t.storeId,
          orderId: t.orderId,
          orderName: t.orderName,
          title: t.title,
          variantTitle: t.variantTitle,
          sizeBand: t.sizeBand,
          resolvedBlankSku: t.resolvedBlankSku,
          quantity: 0,
          lineCancelled: t.lineCancelled,
          qtyDropped: 0,
          placements: [],
        },
        copies: new Set(),
        dropped: new Set(),
        byLocation: new Map(),
      };
      groups.set(key, g);
    }
    // A garment (shirt) is one COPY of one line; count distinct copies as the quantity.
    g.copies.add(t.copyIndex);
    // A refunded/removed copy is still on the film but must not be pressed — track it separately.
    if (t.lineQtyDropped) g.dropped.add(t.copyIndex);
    // Every copy of a line shares the same set of placements (design + size), so the first transfer
    // seen at a location fixes that location's entry — later copies of it are redundant.
    if (!g.byLocation.has(t.location)) {
      g.byLocation.set(t.location, {
        location: t.location,
        designId: t.designId,
        designName: t.designName,
        widthIn: t.widthIn,
        heightIn: t.heightIn,
        fromStock: t.fromStock ?? false,
      });
    }
  }
  return [...groups.values()]
    .map((g) => ({
      ...g.row,
      quantity: g.copies.size,
      qtyDropped: g.dropped.size,
      placements: [...g.byLocation.values()].sort((a, b) => a.location.localeCompare(b.location)),
    }))
    .sort(
      (a, b) =>
        a.orderName.localeCompare(b.orderName) ||
        a.title.localeCompare(b.title) ||
        (a.variantTitle ?? '').localeCompare(b.variantTitle ?? ''),
    );
}

/** One customer order on the sheet, with every garment in it — the unit staff bag and deliver. */
export type ManifestOrderGroup = {
  key: string;
  orderName: string;
  /** Every garment (order line) in this order that landed on this sheet. */
  garments: ManifestGarmentGroup[];
  /** Physical shirts in this order on the sheet (Σ garment quantity, cancelled/refunded included). */
  garmentCount: number;
  /** Shirts actually owed to bag = garmentCount minus cancelled lines and refunded copies. */
  bagCount: number;
  /** Any garment here is for a cancelled order — flag before bagging. */
  hasCancelled: boolean;
  /** Any garment here had copies refunded after batching — bag fewer than the film shows. */
  hasDropped: boolean;
};

/**
 * Group a manifest's transfers by ORDER — the delivery unit — so staff can collate and bag one whole
 * order at a time instead of hunting its garments across the film. Reuses {@link groupTransfersByGarment}
 * (already order-sorted) and buckets those garment rows under their order, carrying a per-order
 * shirt count and the cancelled/refunded flags. Orders appear in the same order-name order as the
 * garment view; garments within an order stay in their garment-view order. Pure regroup — no data the
 * garment/sheet views don't already have.
 */
export function groupTransfersByOrder(m: {
  transfers: readonly BatchManifestTransfer[];
}): ManifestOrderGroup[] {
  const orders = new Map<string, ManifestOrderGroup>();
  for (const g of groupTransfersByGarment(m)) {
    const key = `${g.storeId}#${g.orderId}`;
    let o = orders.get(key);
    if (!o) {
      o = {
        key,
        orderName: g.orderName,
        garments: [],
        garmentCount: 0,
        bagCount: 0,
        hasCancelled: false,
        hasDropped: false,
      };
      orders.set(key, o);
    }
    o.garments.push(g);
    o.garmentCount += g.quantity;
    // A cancelled line owes nothing; a refunded copy is on the film but not owed.
    o.bagCount += g.lineCancelled ? 0 : g.quantity - g.qtyDropped;
    if (g.lineCancelled) o.hasCancelled = true;
    if (g.qtyDropped > 0) o.hasDropped = true;
  }
  return [...orders.values()];
}

// ── Run sheet (production control): DTF + screen-print, by order ──

/** One print on a garment, tagged with the technique it's applied by (`dtf` heat-press vs `screen-print`). */
export type RunSheetPlacement = {
  location: string;
  method: PressMethod;
  designId?: string;
  designName?: string;
  /** DTF only: pressed from a printed transfer pulled from stock (off the sheet). Screen print never is. */
  fromStock: boolean;
};

/** One garment (order line) of the batch, with EVERY print it gets across both techniques — what the
 *  label sheet and the pull list's counts are built from. */
export type RunSheetGarment = {
  key: string;
  storeId: string;
  orderId: string;
  orderName: string;
  title: string;
  variantTitle?: string;
  sizeBand: string;
  resolvedBlankSku?: string;
  /** Physical shirts of this line (identical copies collapse into a count). */
  quantity: number;
  lineCancelled: boolean;
  /** Copies refunded/removed after batching — on the film but NOT to be pressed. */
  qtyDropped: number;
  /** DTF + screen-print prints, sorted by location then technique. */
  placements: RunSheetPlacement[];
};

/** One customer order on the batch, with its garments — the unit staff pull, press, and collate. */
export type RunSheetOrder = {
  key: string;
  orderName: string;
  garments: RunSheetGarment[];
  /** Physical shirts on the sheet (Σ garment quantity, cancelled/refunded included). */
  garmentCount: number;
  /** Shirts actually owed = garmentCount minus cancelled lines and refunded copies. */
  bagCount: number;
  hasCancelled: boolean;
  hasDropped: boolean;
};

/**
 * Merge a batch's DTF placements (the manifest transfers, technique `dtf`) with its screen-print work
 * (technique `screen-print`) into one per-garment list. EVERY batched line has ≥1 DTF placement — a
 * line whose placements are all screen-print is blocked at build time ("nothing to gang-sheet") — so a
 * screen-print item only ADDS a location to a garment the manifest already carries, and that garment's
 * cancelled/refunded flags (computed from the live order line) cover its screen-print prints too. The
 * `else` branch is defensive only; it should never fire for a well-formed batch.
 */
export function runSheetGarments(
  m: { transfers: readonly BatchManifestTransfer[] },
  screenPrintWork: readonly ScreenPrintWorkItem[] = [],
): RunSheetGarment[] {
  // Keyed by the garment's line identity `store#order#line` — which IS `ManifestGarmentGroup.key`
  // and the same shape a screen-print item builds below, so the two techniques merge per line.
  const byLine = new Map<string, RunSheetGarment>();
  for (const g of groupTransfersByGarment(m)) {
    byLine.set(g.key, {
      key: g.key,
      storeId: g.storeId,
      orderId: g.orderId,
      orderName: g.orderName,
      title: g.title,
      variantTitle: g.variantTitle,
      sizeBand: g.sizeBand,
      resolvedBlankSku: g.resolvedBlankSku,
      quantity: g.quantity,
      lineCancelled: g.lineCancelled,
      qtyDropped: g.qtyDropped,
      placements: g.placements.map((p) => ({
        location: p.location,
        method: 'dtf' as PressMethod,
        designId: p.designId,
        designName: p.designName,
        fromStock: p.fromStock,
      })),
    });
  }
  for (const sp of screenPrintWork) {
    const key = `${sp.storeId}#${sp.orderId}#${sp.lineId}`;
    const placement: RunSheetPlacement = {
      location: sp.location,
      method: 'screen-print',
      designId: sp.designId,
      designName: sp.designName,
      fromStock: false,
    };
    const g = byLine.get(key);
    if (g) {
      // Don't double-add if this SP location is somehow already present.
      if (!g.placements.some((p) => p.location === sp.location && p.method === 'screen-print'))
        g.placements.push(placement);
    } else {
      byLine.set(key, {
        key,
        storeId: sp.storeId,
        orderId: sp.orderId,
        orderName: sp.orderName,
        title: sp.title,
        variantTitle: sp.variantTitle,
        sizeBand: sp.sizeBand,
        resolvedBlankSku: sp.resolvedBlankSku,
        quantity: sp.quantity,
        lineCancelled: false,
        qtyDropped: 0,
        placements: [placement],
      });
    }
  }
  const garments = [...byLine.values()];
  for (const g of garments)
    g.placements.sort(
      (a, b) => a.location.localeCompare(b.location) || a.method.localeCompare(b.method),
    );
  return garments.sort(
    (a, b) =>
      a.orderName.localeCompare(b.orderName) ||
      a.title.localeCompare(b.title) ||
      (a.variantTitle ?? '').localeCompare(b.variantTitle ?? ''),
  );
}

/**
 * Group {@link runSheetGarments} by ORDER — the run sheet's unit (pull · print · press · collate one
 * order at a time). Carries the per-order shirt counts + cancelled/refunded flags, same semantics as
 * {@link groupTransfersByOrder} but over the DTF+screen-print merged garments.
 */
export function runSheetOrders(
  m: { transfers: readonly BatchManifestTransfer[] },
  screenPrintWork: readonly ScreenPrintWorkItem[] = [],
): RunSheetOrder[] {
  const orders = new Map<string, RunSheetOrder>();
  for (const g of runSheetGarments(m, screenPrintWork)) {
    const key = `${g.storeId}#${g.orderId}`;
    let o = orders.get(key);
    if (!o) {
      o = {
        key,
        orderName: g.orderName,
        garments: [],
        garmentCount: 0,
        bagCount: 0,
        hasCancelled: false,
        hasDropped: false,
      };
      orders.set(key, o);
    }
    o.garments.push(g);
    o.garmentCount += g.quantity;
    o.bagCount += g.lineCancelled ? 0 : g.quantity - g.qtyDropped;
    if (g.lineCancelled) o.hasCancelled = true;
    if (g.qtyDropped > 0) o.hasDropped = true;
  }
  return [...orders.values()];
}

/**
 * Which confirmation the mark-printed route is demanding, if any. The server exposes TWO distinct
 * consents and they are not interchangeable — a reissue flips no order lines, so `confirmBulkSettle`
 * never satisfies the reprint gate and vice versa. Echoing back the wrong flag re-trips the gate,
 * which reads to the operator as a sheet that cannot be closed at all.
 *
 *  · `bulkSettle`   — R1.1: a normal sheet still owns BATCHED lines with no press record; closing
 *                     flips them PRINTED and draws their blanks with no who/when.
 *  · `reprintClose` — P0: a REPRINT sheet has transfers with no press record; closing releases their
 *                     orders for shipping and draws the replacement blanks.
 */
export type MarkPrintedConfirmKind = 'bulkSettle' | 'reprintClose';

export function markPrintedConfirmKind(
  errorType: string | undefined,
): MarkPrintedConfirmKind | null {
  if (errorType === 'bulk_settle_confirmation_required') return 'bulkSettle';
  if (errorType === 'reprint_close_confirmation_required') return 'reprintClose';
  return null;
}

/** The retry body for a confirmation kind — the ONLY place the flag name is chosen. */
export function markPrintedConfirmBody(
  kind: MarkPrintedConfirmKind,
): { confirmBulkSettle: true } | { confirmReprintClose: true } {
  return kind === 'reprintClose' ? { confirmReprintClose: true } : { confirmBulkSettle: true };
}

// ── Pulled blanks v2 — the pull station's state, from the sheet header's record ───────────────────
// `docs/pulled-blanks-pull-station-plan-v2.md` §2.2 / §2.5. The record is a map of bare ssSku → the
// puller's row ({qty, at, by, via, who?}) on the batch header, with three advisory first-time stamps.
// Everything below is pure: the chip, the rail, the press station and the overlay all read one rule.

export type BatchPulledMap = NonNullable<BatchSummary['pulled']>;
export type BatchPulledRow = BatchPulledMap[string];
export type PullStampKind = 'pull-list' | 'labels' | 'station';
export type BatchPulledVia = components['schemas']['BatchPulledRequest']['via'];

export type PullStateInput = Pick<
  BatchSummary,
  | 'status'
  | 'pulled'
  | 'pulledAt'
  | 'pulledRev'
  | 'pullListPrintedAt'
  | 'labelsPrintedAt'
  | 'pullStationOpenedAt'
>;

/** One pull-list row against the record: what the list says to pull and what the puller recorded. */
export type PullRowState = {
  row: BlankPull;
  /** The puller's row, when ticked. Absent on a row with no blank on file (it cannot be recorded). */
  recorded?: BatchPulledRow;
  /** `listed − recorded` when the pull came up short (a short is recorded as what was taken). */
  short: number;
  /** `recorded − listed` when the sheet stopped owing copies after the pull (a refund) — still out. */
  over: number;
};

/** A row's short or leftover, named for the chip and the close's question. */
export type PullShort = {
  ssSku: string;
  label: string;
  variant: string;
  got: number;
  listed: number;
};

export type PullState =
  /** DRAFT / COMPOSITING / FAILED — "nothing to pull until the sheet is READY". */
  | { kind: 'not-ready' }
  /** No stamp, no record: its blanks are shelf; the count asks nothing. */
  | { kind: 'not-started' }
  /** A stamp (printed, or the station opened) and no record — amber; the one state the count asks about. */
  | { kind: 'started'; printed: Exclude<PullStampKind, 'station'>[]; at: string }
  /** A record. `rows` / `remaining` / `shorts` / `stillOut` need the listed rows; a caller without them
   *  (the rail, the press station) gets the units alone and `complete: undefined`. */
  | {
      kind: 'pulled';
      /** Σ recorded over the record — what is off the shelf and still in the book. */
      units: number;
      /** Σ listed over the rows with a blank on file (the walk's whole). */
      listed?: number;
      /** Rows with a blank on file / those ticked / those still to tick. */
      rows?: number;
      ticked?: number;
      remaining?: number;
      complete?: boolean;
      shorts: PullShort[];
      /** Recorded above what the sheet still owes: a refund after the pull. On a PRINTED sheet, "still out". */
      stillOut: PullShort[];
      /** The latest tick: when, by whom (the login), initials. */
      at: string;
      by?: string;
      who?: string;
    };

/** The record's rows beside the listed rows — the overlay's table and the chip's arithmetic. */
export function pullRowStates(
  rows: readonly BlankPull[],
  pulled: BatchPulledMap | undefined,
): PullRowState[] {
  return rows.map((row) => {
    const recorded = row.ssSku ? pulled?.[row.ssSku] : undefined;
    return {
      row,
      recorded,
      short: recorded ? Math.max(0, row.qty - recorded.qty) : 0,
      over: recorded ? Math.max(0, recorded.qty - row.qty) : 0,
    };
  });
}

function pullShortOf(s: PullRowState, got: number): PullShort {
  return {
    ssSku: s.row.ssSku ?? s.row.key,
    label: s.row.label,
    variant: s.row.variant || s.row.sizeBand,
    got,
    listed: s.row.qty,
  };
}

/**
 * The sheet's pull state (§2.5 door 3's six chip states), from the header alone or, when the listed rows
 * are given, with the walk's arithmetic (mid-walk, short, still out). Nothing here is guessed from READY
 * or from a print: a stamp is evidence the count may ask about, never a record.
 */
export function pullState(b: PullStateInput, rows?: readonly BlankPull[]): PullState {
  if (b.status !== 'READY' && b.status !== 'PRINTED') return { kind: 'not-ready' };
  const entries = Object.entries(b.pulled ?? {});
  if (entries.length === 0) {
    const printed: Exclude<PullStampKind, 'station'>[] = [];
    if (b.pullListPrintedAt) printed.push('pull-list');
    if (b.labelsPrintedAt) printed.push('labels');
    // The strongest evidence is NAMED (a print over the station's open); the sheet is DATED from the
    // earliest stamp — the server's `pullStartedAt`, which the count's question uses — so the card, the
    // rail and the count sheet show one clock for one sheet.
    const at = [b.pullListPrintedAt, b.labelsPrintedAt, b.pullStationOpenedAt]
      .filter((x): x is string => typeof x === 'string' && x.length > 0)
      .sort()[0];
    if (!at) return { kind: 'not-started' };
    return { kind: 'started', printed, at };
  }
  const units = entries.reduce((s, [, r]) => s + r.qty, 0);
  // The latest tick names the time and the hands; initials come from any ticked row (one puller per walk
  // in practice, and the server never overwrites a row's own initials).
  const latest = entries
    .map(([, r]) => r)
    .sort((a, z) => a.at.localeCompare(z.at))
    .at(-1);
  const who = entries.map(([, r]) => r.who).find((w) => w);
  const base = {
    kind: 'pulled' as const,
    units,
    at: latest?.at ?? b.pulledAt ?? '',
    by: latest?.by,
    who,
  };
  if (!rows) return { ...base, shorts: [], stillOut: [] };
  const states = pullRowStates(rows, b.pulled).filter((s) => s.row.ssSku);
  const ticked = states.filter((s) => s.recorded);
  const shorts = ticked.filter((s) => s.short > 0).map((s) => pullShortOf(s, s.recorded?.qty ?? 0));
  const stillOut = ticked
    .filter((s) => s.over > 0)
    .map((s) => pullShortOf(s, s.recorded?.qty ?? 0));
  // A recorded sku no longer on the list at all (every copy refunded after the pull) is still out too.
  const listedSkus = new Set(states.map((s) => s.row.ssSku));
  for (const [ssSku, r] of entries) {
    if (!listedSkus.has(ssSku)) {
      stillOut.push({ ssSku, label: ssSku, variant: '', got: r.qty, listed: 0 });
    }
  }
  return {
    ...base,
    listed: states.reduce((s, x) => s + x.row.qty, 0),
    rows: states.length,
    ticked: ticked.length,
    remaining: states.length - ticked.length,
    complete: states.length - ticked.length === 0,
    shorts,
    stillOut,
  };
}

/** "20m ago", "2h ago", "3d ago" — `relativeAge`'s vocabulary (the card's "Printed 2h" badge), so one header
 *  never shows two clocks; its "just now" and its >30-day date come through as they are. */
export function pullAge(iso: string, nowMs: number): string {
  const r = relativeAge(iso, nowMs);
  return /^\d+[mhd]$/.test(r) ? `${r} ago` : r;
}

function shortLine(s: PullShort): string {
  const name = [s.label, s.variant.replace(/ · /g, ' ')].filter(Boolean).join(' ');
  return `${name} ${s.got} of ${s.listed}`;
}

/**
 * The chip's words (§5). `long` is the card and the run page; `short` is the rail and the press station's
 * batch filter. `status` tells a leftover apart from a refund on an open sheet.
 */
export function pullChipLabel(
  state: PullState,
  status: BatchStatus,
  nowMs: number,
  form: 'long' | 'short' = 'long',
): string {
  switch (state.kind) {
    case 'not-ready':
      return form === 'long' ? 'nothing to pull until the sheet is READY' : '';
    case 'not-started':
      return 'not started';
    case 'started': {
      const what =
        state.printed.length === 0
          ? 'pull station opened'
          : `${state.printed.map((p) => (p === 'pull-list' ? 'pull list' : 'labels')).join(' + ')} printed`;
      return form === 'long'
        ? `${what} ${pullAge(state.at, nowMs)} · not confirmed`
        : `${state.printed.length === 0 ? 'opened' : 'printed'} · not confirmed`;
    }
    case 'pulled': {
      if (status === 'PRINTED' && state.stillOut.length > 0) {
        const n = state.stillOut.reduce((s, x) => s + (x.got - x.listed), 0);
        return form === 'long' ? `${n} still out — returned to the shelf?` : `${n} still out`;
      }
      const head =
        state.complete === false
          ? `Pulled ${state.units} of ${state.listed}`
          : `Pulled ${state.units}`;
      if (form === 'short') {
        return state.shorts.length > 0 ? `${head} · ${state.shorts.length} short` : head;
      }
      const parts = [head];
      if (state.complete === false && state.remaining) {
        parts.push(`${state.remaining} row${state.remaining === 1 ? '' : 's'} to tick`);
      }
      for (const s of state.shorts) parts.push(`short: ${shortLine(s)}`);
      if (state.complete !== false && state.at) parts.push(pullAge(state.at, nowMs));
      if (state.who) parts.push(`by ${state.who}`);
      return parts.join(' · ');
    }
  }
}

/** One `POST /batches/:id/pulled` body's rows: the COMPLETE record after this change (§2.3 — a row omitted
 *  is un-ticked). `change` sets one sku's quantity (`null` unticks it); every other recorded sku is sent
 *  back unchanged — including one no longer on the list, which is off the shelf whatever the sheet now
 *  owes. Never a cached quantity for the changed row: the caller passes what the puller tapped or typed. */
export function pullRecordRows(
  pulled: BatchPulledMap | undefined,
  change: { ssSku: string; qty: number | null },
): { ssSku: string; qty: number }[] {
  const rows: { ssSku: string; qty: number }[] = [];
  for (const [ssSku, r] of Object.entries(pulled ?? {})) {
    if (ssSku === change.ssSku) continue;
    rows.push({ ssSku, qty: r.qty });
  }
  if (change.qty !== null && change.qty > 0) rows.push({ ssSku: change.ssSku, qty: change.qty });
  return rows;
}

/** The close's leftover question (§2.5 door 6 / §2.6): every recorded sku above what the sheet still
 *  owes, and the rows that "Returned to the shelf" would post — each such sku reduced to its listed
 *  quantity (a sku no longer listed drops to 0, i.e. is removed). */
export function pullLeftovers(
  pulled: BatchPulledMap | undefined,
  rows: readonly BlankPull[],
): { leftovers: PullShort[]; returnedRows: { ssSku: string; qty: number }[] } {
  const listedBySku = new Map<string, BlankPull>();
  for (const r of rows) if (r.ssSku) listedBySku.set(r.ssSku, r);
  const leftovers: PullShort[] = [];
  const returnedRows: { ssSku: string; qty: number }[] = [];
  for (const [ssSku, r] of Object.entries(pulled ?? {})) {
    const listed = listedBySku.get(ssSku);
    const owed = listed?.qty ?? 0;
    if (r.qty > owed) {
      leftovers.push({
        ssSku,
        label: listed?.label ?? ssSku,
        variant: listed ? listed.variant || listed.sizeBand : '',
        got: r.qty,
        listed: owed,
      });
      if (owed > 0) returnedRows.push({ ssSku, qty: owed });
    } else {
      returnedRows.push({ ssSku, qty: r.qty });
    }
  }
  return { leftovers, returnedRows };
}

/**
 * Garments on this sheet whose blank the press has already drawn — a LINE is settled (its blank consumed)
 * once every placement of a copy is pressed, so a copy counts when each of its line's placements is
 * pressed: `pressedAt` = every copy, else the copy indexes in `pressedCopies`. Screen-print placements
 * (off the sheet) carry only `pressedAt`. Feeds the delete dialog's warning: deleting hands these blanks
 * back to the book for shirts that exist (the unbuild reverses the print's consumes).
 */
export function pressedGarmentCount(
  items: readonly Pick<
    BatchItemSummary,
    | 'storeId'
    | 'orderId'
    | 'lineId'
    | 'quantity'
    | 'pressedAt'
    | 'pressedCopies'
    | 'reissueTransferOnly'
  >[],
  screenPrintWork: readonly Pick<
    ScreenPrintWorkItem,
    'storeId' | 'orderId' | 'lineId' | 'quantity' | 'pressedAt'
  >[] = [],
): number {
  // Per line: one entry per placement — 'all' (pressedAt) or the set of pressed copy indexes.
  const lines = new Map<string, { parts: Array<Set<number> | 'all'>; quantity: number }>();
  const key = (x: { storeId: string; orderId: string; lineId: string }) =>
    `${x.storeId}#${x.orderId}#${x.lineId}`;
  for (const it of items) {
    // A transfer-only reprint presses onto the shirt already on the floor and consumes no blank — the
    // same exclusion `blanksToPull` and the server's `sheetBlanks` make.
    if (it.reissueTransferOnly) continue;
    const k = key(it);
    const l = lines.get(k) ?? { parts: [], quantity: it.quantity };
    l.parts.push(it.pressedAt ? 'all' : new Set((it.pressedCopies ?? []).filter((c) => c >= 0)));
    lines.set(k, l);
  }
  const spByLine = new Map<string, { quantity: number; all: boolean }>();
  for (const w of screenPrintWork) {
    const k = key(w);
    const prev = spByLine.get(k);
    spByLine.set(k, {
      quantity: w.quantity,
      all: (prev?.all ?? true) && w.pressedAt != null,
    });
  }
  let total = 0;
  const seen = new Set<string>();
  for (const [k, l] of lines) {
    seen.add(k);
    const sp = spByLine.get(k);
    if (sp && !sp.all) continue; // a mixed line's screen print is not pressed → no copy is complete
    let n = 0;
    for (let copy = 0; copy < l.quantity; copy += 1) {
      if (l.parts.every((p) => p === 'all' || p.has(copy))) n += 1;
    }
    total += n;
  }
  for (const [k, sp] of spByLine) {
    if (seen.has(k)) continue;
    if (sp.all) total += sp.quantity;
  }
  return total;
}
